import React from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { HELP } from "@/lib/help";

/* Guide d'utilisation (FAQ) par rôle — modal-sheet mobile-first.
   Ouvert depuis l'en-tête (icône « ? ») et automatiquement après l'activation. */
export function HelpGuide({ open, onClose }) {
  const { lang, t } = useApp();
  const { user } = useAuth();
  if (!open) return null;

  const data = HELP[lang] || HELP.fr;
  const role = user?.role || "coproprietaire";
  const roleData = data.roles[role] || data.roles.coproprietaire;
  const roleLabel = t("role_" + role);

  const Item = ({ it }) => {
    const Ico = Icons[it.icon] || Icons.Dot;
    return (
      <div className="flex gap-3 items-start" data-testid="help-item">
        <span className="h-9 w-9 rounded-xl bg-brand/10 text-brand dark:text-primary flex items-center justify-center shrink-0">
          <Ico className="h-4 w-4" />
        </span>
        <p className="text-sm leading-relaxed pt-1.5">{it.text}</p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose} data-testid="help-guide">
      <div className="bg-card text-foreground rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-card px-5 py-4 border-b border-border flex items-center justify-between z-10">
          <div className="min-w-0">
            <h2 className="font-black text-lg truncate">{data.chrome.title}</h2>
            <p className="text-xs text-muted-foreground truncate">{data.chrome.subtitle}</p>
          </div>
          <button onClick={onClose} data-testid="help-close"
            className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
            <Icons.X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <div className="rounded-2xl bg-brand text-white p-4 relative overflow-hidden">
            <div className="absolute inset-0 zellige-overlay" />
            <div className="relative z-10">
              <span className="text-xs font-semibold uppercase tracking-wide text-white/70">{data.chrome.your_role}</span>
              <p className="font-black text-lg">{roleLabel}</p>
              <p className="text-sm text-white/85 mt-0.5">{roleData.tagline}</p>
            </div>
          </div>

          <div>
            <p className="font-bold mb-3">{data.chrome.canDo}</p>
            <div className="space-y-3">
              {roleData.items.map((it) => <Item key={it.text} it={it} />)}
            </div>
          </div>

          <div>
            <p className="font-bold mb-3">{data.chrome.everyone}</p>
            <div className="space-y-3">
              {data.common.map((it) => <Item key={it.text} it={it} />)}
            </div>
          </div>

          <button onClick={onClose} data-testid="help-gotit"
            className="w-full h-12 rounded-xl bg-brand text-white font-bold active:scale-[0.98] transition-transform">
            {data.chrome.gotit}
          </button>
        </div>
      </div>
    </div>
  );
}
